import React, { useState, useEffect, useRef } from 'react';
import { useDataContext } from '../../context/DataContext';
import { useOrg } from '../../context/OrgContext';
import { Order, OrderStatus } from '../../types';
import Notification from '../../features/notifications/Notification';

const KitchenCabinet: React.FC = () => {
  const { orders, loadingOrders, updateOrderStatus, refreshOrders, deleteOrder, orderLimits, refreshOrderLimits } = useDataContext();
  const { currentOrganization } = useOrg();
  const [filter, setFilter] = useState<OrderStatus | 'all'>('pending');
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [previousOrderCount, setPreviousOrderCount] = useState<number>(0);
  const [newOrderIds, setNewOrderIds] = useState<Set<string>>(new Set());
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [confirmRejectOrder, setConfirmRejectOrder] = useState<Order | null>(null);

  // Kitchen notifications stack (persistent until closed)
  const [kitchenNotifications, setKitchenNotifications] = useState<
    Array<{ id: string; orderIds: string[]; createdAt: number; summary: string }>
  >([]);

  // Sound alerts toggle persisted in localStorage
  const [soundEnabled, setSoundEnabled] = useState<boolean>(() =>
    typeof window !== 'undefined'
      ? localStorage.getItem('kitchenSoundEnabled') !== 'false'
      : true
  );
  const [showSoundHelp, setShowSoundHelp] = useState<boolean>(false);
  const [soundVolume, setSoundVolume] = useState<number>(() => {
    if (typeof window === 'undefined') return 1;
    const raw = localStorage.getItem('kitchenSoundVolume');
    const v = raw ? Number(raw) : 1;
    return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 1;
  });
  const [soundMenuOpen, setSoundMenuOpen] = useState<boolean>(false);
  const soundMenuBtnRef = useRef<HTMLButtonElement | null>(null);
  const soundMenuRef = useRef<HTMLDivElement | null>(null);
  const [soundMenuStyle, setSoundMenuStyle] = useState<React.CSSProperties>({});
  const [soundFile, setSoundFile] = useState<string>(() => {
    if (typeof window === 'undefined') return '/sounds/new-order.mp3';
    return localStorage.getItem('kitchenSoundFile') || '/sounds/new-order.mp3';
  });

  // Fallback bell-like chime using Web Audio API (short, pleasant, restaurant bell)
  const playBellChime = React.useCallback(() => {
    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      const ctx = new AudioCtx();
      const now = ctx.currentTime;

      const createPartial = (frequency: number, startGain: number, endTimeOffset: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(frequency, now);
        osc.frequency.exponentialRampToValueAtTime(frequency * 0.985, now + endTimeOffset);
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(startGain, now + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + endTimeOffset);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + endTimeOffset + 0.02);
      };

      // Fundamental and a couple of overtones for a bell feel
      // Duration ~0.6s for fundamental, shorter for overtones
      createPartial(880, 0.18, 0.6);   // A5 fundamental
      createPartial(1320, 0.12, 0.45); // perfect fifth overtone
      createPartial(1760, 0.08, 0.35); // octave overtone

      // Very light second strike after 120ms
      setTimeout(() => {
        try {
          const n2 = ctx.currentTime;
          const osc2 = ctx.createOscillator();
          const g2 = ctx.createGain();
          osc2.type = 'sine';
          osc2.frequency.setValueAtTime(880, n2);
          g2.gain.setValueAtTime(0.0001, n2);
          g2.gain.exponentialRampToValueAtTime(0.08, n2 + 0.01);
          g2.gain.exponentialRampToValueAtTime(0.0001, n2 + 0.25);
          osc2.connect(g2);
          g2.connect(ctx.destination);
          osc2.start(n2);
          osc2.stop(n2 + 0.27);
        } catch {}
      }, 120);
    } catch (e) {
      // Ignore if WebAudio unavailable
    }
  }, []);

  const isAutoplayBlockedError = (error: any): boolean => {
    const msg = (error?.message || '').toString().toLowerCase();
    const name = (error?.name || '').toString();
    return (
      name === 'NotAllowedError' ||
      msg.includes('gesture') ||
      msg.includes('autoplay') ||
      msg.includes('user interaction') ||
      msg.includes('user gesture')
    );
  };

  const playNotificationSound = React.useCallback(() => {
    try {
      const audio = new Audio(soundFile);
      audio.volume = soundVolume;
      audio.play()
        .then(() => {
          setShowSoundHelp(false);
        })
        .catch((e) => {
          if (isAutoplayBlockedError(e)) {
            setShowSoundHelp(true);
          } else {
            // File missing/decoding/network issues: don't show autoplay tip
            setShowSoundHelp(false);
          }
          playBellChime();
        });
    } catch (e) {
      // Creating Audio failed: not typical; attempt beep and maybe show tip if it's clearly autoplay related
      if (isAutoplayBlockedError(e)) {
        setShowSoundHelp(true);
      }
      playBellChime();
    }
  }, [playBellChime, soundVolume, soundFile]);

  // Auto-hide the sound help after a short time and on user interaction
  useEffect(() => {
    if (!showSoundHelp) return;
    const timer = setTimeout(() => setShowSoundHelp(false), 8000);
    const handler = () => setShowSoundHelp(false);
    document.addEventListener('click', handler);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handler);
    };
  }, [showSoundHelp]);

  // Close the sound menu on outside click or Escape
  useEffect(() => {
    if (!soundMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const menu = soundMenuRef.current;
      const btn = soundMenuBtnRef.current;
      if (!menu || !btn) return;
      const target = e.target as Node;
      if (!menu.contains(target) && !btn.contains(target)) {
        setSoundMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSoundMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [soundMenuOpen]);

  // Reposition the sound menu to stay within viewport (mobile-friendly)
  useEffect(() => {
    if (!soundMenuOpen) return;
    const timer = setTimeout(() => {
      const menu = soundMenuRef.current;
      const btn = soundMenuBtnRef.current;
      if (!menu || !btn) return;
      const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
      const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
      const btnRect = btn.getBoundingClientRect();
      const width = Math.max(240, Math.min(320, vw - 16));

      // Initial placement: fixed below, left-aligned to the bell button
      let left = Math.max(8, Math.min(vw - width - 8, btnRect.left));
      let top = Math.min(vh - 8, btnRect.bottom + 8);

      // Apply initial size for accurate measurement
      setSoundMenuStyle({ position: 'fixed', left, top, width, zIndex: 2000 });

      // After paint, adjust if necessary (e.g., open upwards)
      requestAnimationFrame(() => {
        const r = menu.getBoundingClientRect();
        let newLeft = left;
        let newTop = top;

        if (r.right > vw - 8) newLeft = Math.max(8, vw - r.width - 8);
        if (r.left < 8) newLeft = 8;
        if (r.bottom > vh - 8) newTop = Math.max(8, btnRect.top - r.height - 8);

        setSoundMenuStyle({ position: 'fixed', left: newLeft, top: newTop, width: r.width, zIndex: 2000 });
      });
    }, 0);
    return () => clearTimeout(timer);
  }, [soundMenuOpen]);

  useEffect(() => {
    try {
      localStorage.setItem('kitchenSoundEnabled', String(soundEnabled));
    } catch (error) {
      // Ignore write failures (e.g., privacy mode)
    }
  }, [soundEnabled]);

  useEffect(() => {
    try { localStorage.setItem('kitchenSoundVolume', String(soundVolume)); } catch {}
  }, [soundVolume]);
  useEffect(() => {
    try { localStorage.setItem('kitchenSoundFile', soundFile); } catch {}
  }, [soundFile]);

  // Refs to manage diffing, throttling, and initial hydration
  const previousOrderIdsRef = useRef<Set<string>>(new Set());
  const initialHydratedRef = useRef<boolean>(false);
  const lastDetectionAtRef = useRef<number>(0);
  const lastSoundAtRef = useRef<number>(0);
  const firstFetchStartedRef = useRef<boolean>(false);

  // Track when the first fetch actually starts, so baseline uses completed data
  useEffect(() => {
    if (loadingOrders) {
      firstFetchStartedRef.current = true;
    }
  }, [loadingOrders]);

  // Auto-refresh orders every 5 seconds for real-time updates
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      refreshOrders();
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshOrders]);

  // Initial load of orders
  useEffect(() => {
    refreshOrders();
  }, [refreshOrders]);

  // Detect new orders, highlight for 20s, group notifications within 5s, and play sound once per window
  useEffect(() => {
    const currentIds = new Set(orders.map(o => o.id));

    // Skip notifications until we have completed the first actual fetch
    if (!initialHydratedRef.current) {
      if (!firstFetchStartedRef.current || loadingOrders) {
        return; // wait until first fetch has started and completed
      }
      previousOrderIdsRef.current = currentIds;
      initialHydratedRef.current = true;
      setPreviousOrderCount(orders.length);
      return;
    }

    // Diff: ids that are in current but not in previous
    const prevIds = previousOrderIdsRef.current;
    const newlyAddedIds = Array.from(currentIds).filter(id => !prevIds.has(id));

    if (newlyAddedIds.length > 0) {
      // Highlight these orders
      setNewOrderIds(prev => new Set([...Array.from(prev), ...newlyAddedIds]));
      const idsToRemove = new Set(newlyAddedIds);
      setTimeout(() => {
        setNewOrderIds(prev => {
          const updated = new Set(prev);
          idsToRemove.forEach(id => updated.delete(id));
          return updated;
        });
      }, 20000);

      // Group notifications within a 5s window
      const now = Date.now();
      const withinWindow = now - lastDetectionAtRef.current <= 5000;
      lastDetectionAtRef.current = now;

      const summarizeOrder = (orderId: string): string => {
        const order = orders.find(o => o.id === orderId);
        if (!order) return `#${orderId.slice(-6).toUpperCase()}`;
        const idShort = `#${order.id.slice(-6).toUpperCase()}`;
        const customer = order.customerName ? ` • ${order.customerName}` : '';
        const table = order.tableName ? ` • Table ${order.tableName}` : '';
        const items = order.items
          .slice(0, 5)
          .map(i => `${i.quantity}× ${i.menuItem?.name || 'Item'}`)
          .join(', ');
        const moreCount = Math.max(0, order.items.length - 5);
        const totalRaw = (order.total ?? order.totalAmount ?? 0) as number;
        const total = totalRaw ? ` • $${totalRaw.toFixed(2)}` : '';
        return `${idShort}${customer}${table} • ${items}${moreCount > 0 ? ` • +${moreCount} more` : ''}${total}`;
      };

      const summaries = newlyAddedIds.map(summarizeOrder);

      setKitchenNotifications(prev => {
        if (withinWindow && prev.length > 0) {
          // Merge into the latest notification
          const last = prev[0];
          const mergedIds = Array.from(new Set([...last.orderIds, ...newlyAddedIds]));
          const mergedSummary = mergedIds.length > 1
            ? `${mergedIds.length} new orders: ${mergedIds
                .slice(0, 5)
                .map(id => `#${id.slice(-6).toUpperCase()}`)
                .join(', ')}${mergedIds.length > 5 ? `, +${mergedIds.length - 5} more` : ''}`
            : summaries[0];
          const updatedLast = { ...last, orderIds: mergedIds, summary: mergedSummary };
          return [updatedLast, ...prev.slice(1)];
        }

        // Create a new notification
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const summary = newlyAddedIds.length > 1
          ? `${newlyAddedIds.length} new orders: ${newlyAddedIds
              .slice(0, 5)
              .map(id => `#${id.slice(-6).toUpperCase()}`)
              .join(', ')}${newlyAddedIds.length > 5 ? `, +${newlyAddedIds.length - 5} more` : ''}`
          : summaries[0];
        return [{ id, orderIds: newlyAddedIds, createdAt: now, summary }, ...prev];
      });

      // Attempt to play sound if enabled and throttled
      if (soundEnabled) {
        const canPlay = now - lastSoundAtRef.current > 5000;
        if (canPlay) {
          try {
            lastSoundAtRef.current = now;
            playNotificationSound();
          } catch (e) {
            // Fall back
            playNotificationSound();
          }
        }
      }
    }

    // Update refs for next diff
    previousOrderIdsRef.current = currentIds;
    setPreviousOrderCount(orders.length);
  }, [orders, soundEnabled, loadingOrders]);

  const getFilteredOrders = (): Order[] => {
    const twelveHoursAgo = new Date().getTime() - (12 * 60 * 60 * 1000);
    
    // Filter orders from last 12 hours
    const recentOrders = orders.filter(order => order.timestamp >= twelveHoursAgo);
    
    const filteredOrders = filter === 'all' 
      ? recentOrders 
      : recentOrders.filter(order => order.status === filter);
    
    // Sort by timestamp (oldest first) and by status priority
    return filteredOrders.sort((a, b) => {
      const statusPriority = { pending: 0, preparing: 1, ready: 2, completed: 3 };
      if (a.status !== b.status) {
        return statusPriority[a.status] - statusPriority[b.status];
      }
      return a.timestamp - b.timestamp; // Oldest first
    });
  };

  const getStatusCounts = () => {
    const twelveHoursAgo = new Date().getTime() - (12 * 60 * 60 * 1000);
    const recentOrders = orders.filter(order => order.timestamp >= twelveHoursAgo);
    
    return {
      all: recentOrders.length,
      pending: recentOrders.filter(o => o.status === 'pending').length,
      preparing: recentOrders.filter(o => o.status === 'preparing').length,
      ready: recentOrders.filter(o => o.status === 'ready').length,
      completed: recentOrders.filter(o => o.status === 'completed').length,
    };
  };

  const getCompletedOrdersHistory = (): Order[] => {
    const completedOrders = orders.filter(order => order.status === 'completed');
    return completedOrders.sort((a, b) => b.timestamp - a.timestamp); // Newest completed first
  };

  const getNextStatus = (currentStatus: OrderStatus): OrderStatus | null => {
    switch (currentStatus) {
      case 'pending': return 'preparing';
      case 'preparing': return 'ready';
      case 'ready': return 'completed';
      case 'completed': return null;
      default: return null;
    }
  };

  const getStatusColor = (status: OrderStatus): string => {
    switch (status) {
      case 'pending': return '#f39c12';
      case 'preparing': return '#e74c3c';
      case 'ready': return '#3498db';
      case 'completed': return '#27ae60';
      default: return '#95a5a6';
    }
  };

  const formatTime = (timestamp: number): string => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTimeElapsed = (timestamp: number): string => {
    const now = new Date().getTime();
    const orderTime = timestamp;
    const diffInMinutes = Math.floor((now - orderTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const hours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;
    return `${hours}h ${minutes}m ago`;
  };

  const getOrderPriority = (timestamp: number): 'urgent' | 'warning' | 'normal' => {
    const now = new Date().getTime();
    const diffInMinutes = Math.floor((now - timestamp) / (1000 * 60));
    
    if (diffInMinutes >= 30) return 'urgent'; // 30+ minutes
    if (diffInMinutes >= 15) return 'warning'; // 15-29 minutes
    return 'normal'; // 0-14 minutes
  };

  const statusCounts = getStatusCounts();
  const filteredOrders = getFilteredOrders();

  return (
    <div className="fade-in">
      {/* Kitchen Notifications Stack (persistent) */}
      {kitchenNotifications.length > 0 && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
          maxWidth: '420px'
        }} role="status" aria-live="polite">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.25rem' }}>
            {kitchenNotifications.length > 1 && (
              <button
                onClick={() => setKitchenNotifications([])}
                style={{
                  background: '#f1f5f9',
                  color: '#334155',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  padding: '4px 8px',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                }}
                aria-label="Dismiss all notifications"
              >
                Dismiss all
              </button>
            )}
          </div>
          {kitchenNotifications.map(n => (
            <Notification
              key={n.id}
              type="info"
              autoClose={false}
              closeOnClick={true}
              icon={<span role="img" aria-label="Bell">🔔</span>}
              onClose={() => setKitchenNotifications(prev => prev.filter(x => x.id !== n.id))}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontWeight: 700, color: '#1d4ed8' }}>
                  {(() => {
                    const ids = n.orderIds;
                    if (ids.length > 1) {
                      return `${ids.length} new orders`;
                    }
                    const order = orders.find(o => o.id === ids[0]);
                    const idShort = `#${ids[0].slice(-6).toUpperCase()}`;
                    const name = order?.customerName ? ` • ${order.customerName}` : '';
                    const table = order?.tableName ? ` • ${order.tableName}` : '';
                    return `${idShort}${name}${table}`;
                  })()}
                </div>
                <div style={{ color: '#1e3a8a', fontWeight: 600 }}>
                  {(() => {
                    const order = orders.find(o => o.id === n.orderIds[0]);
                    if (!order) return n.summary;
                    const items = order.items
                      .slice(0, 5)
                      .map(i => `${i.quantity}× ${i.menuItem?.name || 'Item'}`)
                      .join(', ');
                    const moreCount = Math.max(0, order.items.length - 5);
                    const totalRaw = (order.total ?? order.totalAmount ?? 0) as number;
                    const total = totalRaw ? ` • $${totalRaw.toFixed(2)}` : '';
                    return `${items}${moreCount > 0 ? `, +${moreCount} more` : ''}${total}`;
                  })()}
                </div>
              </div>
            </Notification>
          ))}
        </div>
      )}

      {/* Header with stats and controls */}
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ flex: '1', minWidth: '300px' }}>
              <h2 className="card-title" style={{ margin: '0 0 0.5rem 0', fontSize: '1.75rem', lineHeight: '1.2' }}>
                Kitchen Dashboard
              </h2>
              <p style={{ margin: 0, color: '#666', fontSize: '1rem', lineHeight: '1.4' }}>
                Manage and track all incoming orders in real-time
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', position: 'relative' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                fontSize: '0.9rem',
                padding: '0.5rem 0.75rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                border: '1px solid #e9ecef'
              }}>
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                />
                Auto-refresh
              </label>
              {showSoundHelp && (
                <div style={{ fontSize: '0.8rem', color: '#d97706', background: '#fffbeb', border: '1px solid #fed7aa', padding: '0.4rem 0.6rem', borderRadius: '6px' }}>
                  Enable audio autoplay in browser or click once on the page to allow sound.
                </div>
              )}
              <button
                onClick={refreshOrders}
                style={{
                  padding: '0.75rem 1.25rem',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                🔄 Refresh
              </button>
              {/* Sound options menu */}
              <div style={{ position: 'relative' }}>
                <button
                  ref={soundMenuBtnRef}
                  onClick={() => setSoundMenuOpen(v => !v)}
                  aria-haspopup="menu"
                  aria-expanded={soundMenuOpen}
                  aria-label="Sound options"
                  style={{
                    padding: '0.5rem 0.6rem',
                    border: '1px solid #e9ecef',
                    background: '#f8f9fa',
                    borderRadius: 8,
                    cursor: 'pointer',
                    lineHeight: 1,
                  }}
                  title={soundEnabled ? `Sound on • ${Math.round(soundVolume*100)}%` : 'Sound off'}
                >
                  {soundEnabled ? '🔔' : '🔕'}
                </button>
                {soundMenuOpen && (
                  <div role="dialog" aria-modal="true" onClick={() => setSoundMenuOpen(false)} style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 2000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'
                  }}>
                    <div ref={soundMenuRef} onClick={(e) => e.stopPropagation()} style={{
                      width: '100%', maxWidth: 420, background: '#fff', border: '1px solid #e2e8f0',
                      borderRadius: 12, boxShadow: '0 12px 36px rgba(0,0,0,0.2)', padding: '0.75rem'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 0.25rem 0.5rem 0.25rem' }}>
                        <div style={{ fontWeight: 700, color: '#0f172a' }}>Sound settings</div>
                        <button onClick={() => setSoundMenuOpen(false)} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#475569' }}>×</button>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.5rem', borderRadius: 6, cursor: 'pointer' }}>
                        <input type="checkbox" checked={soundEnabled} onChange={(e) => setSoundEnabled(e.target.checked)} />
                        <span>Sound alerts enabled</span>
                      </label>
                      <div style={{ padding: '0.4rem 0.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span role="img" aria-label="Bell">🔔</span>
                          <input type="range" min={0} max={1} step={0.05} value={soundVolume} onChange={(e) => setSoundVolume(Number(e.target.value))} style={{ flex: 1 }} aria-label="Sound volume" />
                          <span style={{ fontSize: '0.8rem', color: '#475569', minWidth: 32, textAlign: 'right' }}>{Math.round(soundVolume * 100)}%</span>
                        </div>
                      </div>
                      <div style={{ padding: '0.4rem 0.5rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%' }}>
                          <span style={{ fontSize: '0.9rem', color: '#334155', minWidth: 76 }}>Bell sound</span>
                          <select value={soundFile} onChange={(e) => setSoundFile(e.target.value)} style={{ flex: 1, padding: '0.35rem 0.5rem', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff' }} aria-label="Choose bell sound">
                            <option value="/sounds/new-order.mp3">Bell 1</option>
                            <option value="/sounds/new-order-2.mp3">Bell 2</option>
                            <option value="/sounds/new-order-3.mp3">Bell 3</option>
                          </select>
                        </label>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '0 0.5rem 0.25rem 0.5rem' }}>
                        <button onClick={() => setSoundMenuOpen(false)} style={{ padding: '0.4rem 0.75rem', border: '1px solid #cbd5e1', background: 'white', color: '#334155', borderRadius: 6, cursor: 'pointer' }}>Close</button>
                        <button onClick={() => { setSoundEnabled(true); lastSoundAtRef.current = 0; playNotificationSound(); }} style={{ padding: '0.4rem 0.75rem', background: '#0ea5e9', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }} title="Play test sound">▶ Test</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ 
                fontSize: '0.9rem', 
                color: '#666',
                padding: '0.5rem 0.75rem',
                backgroundColor: '#e9ecef',
                borderRadius: '6px',
                fontWeight: '500'
              }}>
                Total Orders: {orders.length}
              </div>.
                            
            </div>
          </div>
        </div>
        
        {/* Status Filter Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '0.75rem', 
          flexWrap: 'wrap',
          padding: '1.25rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '12px',
          marginTop: '1rem',
          border: '1px solid #e9ecef'
        }}>
          {(['pending', 'preparing', 'ready', 'completed', 'all'] as const).map(status => {
            const isActive = filter === status;
            const count = statusCounts[status];
            const getStatusIcon = (status: string) => {
              switch (status) {
                case 'pending': return '⏳';
                case 'preparing': return '🔥';
                case 'ready': return '✅';
                case 'completed': return '🎉';
                case 'all': return '📋';
                default: return '📋';
              }
            };
            
            return (
              <button
                key={status}
                onClick={() => setFilter(status)}
                style={{
                  fontSize: '0.9rem',
                  textTransform: 'capitalize',
                  padding: '0.875rem 1.5rem',
                  borderRadius: '10px',
                  border: '2px solid',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  minWidth: '140px',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  ...(isActive ? {
                    backgroundColor: '#007bff',
                    color: 'white',
                    borderColor: '#007bff',
                    boxShadow: '0 4px 12px rgba(0, 123, 255, 0.3)',
                    transform: 'translateY(-1px)'
                  } : {
                    backgroundColor: 'white',
                    color: '#666',
                    borderColor: '#dee2e6'
                  })
                }}
              >
                <span style={{ fontSize: '1.1rem' }}>{getStatusIcon(status)}</span>
                <span>{status}</span>
                <span style={{ 
                  backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : '#e9ecef',
                  color: isActive ? 'white' : '#666',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  minWidth: '24px',
                  textAlign: 'center'
                }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Orders Grid */}
      {filteredOrders.length > 0 ? (
        <div className="dashboard-grid">
          {filteredOrders.map(order => {
            const nextStatus = getNextStatus(order.status);
            
            return (
              <div 
                key={order.id} 
                className={`card order-card ${order.status} ${newOrderIds.has(order.id) ? 'new-order-highlight' : ''} order-priority-${getOrderPriority(order.timestamp)}`}
              >
                <div className="order-header" style={{ marginBottom: '1rem' }}>
                  <div className="order-id" style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                    Order #{order.id.slice(-8).toUpperCase()}
                  </div>
                  <div className={`order-status status-${order.status}`} style={{ fontSize: '0.85rem' }}>
                    {order.status.toUpperCase()}
                  </div>
                </div>

                <div className="order-meta" style={{ marginBottom: '0.75rem', padding: '0.5rem', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: '500' }}>👤 {order.customerName}</span>
                    <span style={{ color: getStatusColor(order.status), fontWeight: '600', fontSize: '0.9rem' }}>
                      {getTimeElapsed(order.timestamp)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem', color: '#666' }}>
                    <span>🕒 {formatTime(order.timestamp)}</span>
                    <span>💰 ${(order.total || order.totalAmount || 0).toFixed(2)}</span>
                  </div>
                </div>

                <div className="order-meta" style={{ marginBottom: '0.75rem', padding: '0.5rem', backgroundColor: '#e8f4fd', borderRadius: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem' }}>
                    <span>
                      <strong>Service:</strong> {order.serviceType === 'dine-in' ? '🍽️ Dine In' : '📦 Takeout'}
                    </span>
                    <span>
                      {order.source === 'table-order' ? '🍽️ QR Order' : '📱 App Order'}
                    </span>
                  </div>
                  {order.tableName && (
                    <div style={{ marginTop: '0.25rem', fontSize: '0.85rem', color: '#666' }}>
                      <strong>Table:</strong> {order.tableName}
                    </div>
                  )}
                </div>

                <div className="order-items" style={{ marginBottom: '1rem' }}>
                  <strong style={{ marginBottom: '0.75rem', display: 'block', fontSize: '0.95rem', color: '#333' }}>📋 Items:</strong>
                  {order.items.map((item, index) => (
                    <div key={index} style={{ 
                      marginBottom: '0.5rem', 
                      fontSize: '0.9rem',
                      padding: '0.5rem',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      borderLeft: '3px solid #007bff'
                    }}>
                      <div style={{ fontWeight: '600', color: '#333' }}>
                        {item.quantity}× {item.menuItem?.name || 'Unknown Item'}
                      </div>
                      {item.specialRequests && (
                        <div style={{ fontSize: '0.8rem', color: '#e74c3c', marginTop: '0.25rem', fontStyle: 'italic' }}>
                          💬 {item.specialRequests}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {(order.specialRequests || order.notes) && (
                  <div style={{ 
                    marginBottom: '1rem', 
                    padding: '0.75rem', 
                    backgroundColor: '#fff3cd', 
                    borderRadius: '6px',
                    border: '1px solid #ffeaa7'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '1.1rem', marginRight: '0.5rem' }}>
                        {order.source === 'table-order' ? '📝' : '💬'}
                      </span>
                      <strong style={{ fontSize: '0.9rem', color: '#856404' }}>
                        {order.source === 'table-order' ? 'Customer Notes:' : 'Special Requests:'}
                      </strong>
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#856404', lineHeight: '1.4' }}>
                      {order.notes || order.specialRequests}
                    </div>
                  </div>
                )}

                <div className="order-actions" style={{ 
                  display: 'flex', 
                  gap: '0.5rem', 
                  flexWrap: 'wrap',
                  padding: '0.75rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  marginTop: '0.5rem'
                }}>
                  {/* Edit Order Button */}
                  <button
                    className="btn btn-info"
                    onClick={() => {
                      setEditingOrder(order);
                      setShowEditModal(true);
                    }}
                    style={{ 
                      fontSize: '0.85rem',
                      padding: '0.4rem 0.8rem',
                      fontWeight: '500'
                    }}
                  >
                    ✏️ Edit Order
                  </button>

                  {/* Reject Button - only for pending orders */}
                  {order.status === 'pending' && (
                    <button
                      className="btn btn-danger"
                      onClick={() => setConfirmRejectOrder(order)}
                      style={{ 
                        fontSize: '0.85rem',
                        padding: '0.4rem 0.8rem',
                        fontWeight: '500'
                      }}
                    >
                      ❌ Reject Order
                    </button>
                  )}

                  {/* Next Stage Button - with color of next stage */}
                  {nextStatus && (
                    <button
                      className={`btn ${nextStatus === 'preparing' ? 'btn-warning' : nextStatus === 'ready' ? 'btn-primary' : 'btn-success'}`}
                      onClick={() => updateOrderStatus(order.id, nextStatus)}
                      style={{ 
                        fontSize: '0.9rem',
                        padding: '0.5rem 1rem',
                        fontWeight: '600'
                      }}
                    >
                      {nextStatus === 'preparing' && '⚡ Start Preparing'}
                      {nextStatus === 'ready' && '✅ Mark Ready'}
                      {nextStatus === 'completed' && '🎉 Complete Order'}
                    </button>
                  )}

                  {/* Revert Button */}
                  {order.status !== 'completed' && order.status !== 'pending' && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        const prevStatus = order.status === 'ready' ? 'preparing' : 'pending';
                        updateOrderStatus(order.id, prevStatus);
                      }}
                      style={{ 
                        fontSize: '0.8rem',
                        padding: '0.4rem 0.8rem'
                      }}
                    >
                      ↩️ Revert
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card" style={{ 
          textAlign: 'center', 
          padding: '4rem 2rem',
          backgroundColor: '#f8f9fa',
          border: '2px dashed #dee2e6',
          borderRadius: '12px'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📋</div>
          <h3 style={{ color: '#666', marginBottom: '1rem', fontSize: '1.5rem' }}>
            {filter === 'all' ? 'No Orders Yet' : `No ${filter} Orders`}
          </h3>
          <p style={{ color: '#999', fontSize: '1rem', maxWidth: '400px', margin: '0 auto' }}>
            {filter === 'all' 
              ? 'Orders will appear here as customers place them. The kitchen will be busy soon!'
              : `Switch to "All" to see orders with other statuses.`
            }
          </p>
        </div>
      )}

      {/* Quick Stats Summary */}
      {orders.length > 0 && (
        <div className="card" style={{ marginTop: '2rem' }}>
          <div className="card-header">
            <h3 className="card-title">Today's Summary</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3498db' }}>
                {orders.length}
              </div>
              <div style={{ color: '#666' }}>Total Orders</div>
            </div>
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f39c12' }}>
                {statusCounts.pending}
              </div>
              <div style={{ color: '#666' }}>Pending</div>
            </div>
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#e74c3c' }}>
                {statusCounts.preparing}
              </div>
              <div style={{ color: '#666' }}>In Progress</div>
            </div>
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#27ae60' }}>
                {statusCounts.completed}
              </div>
              <div style={{ color: '#666' }}>Completed</div>
            </div>
            {orderLimits && orderLimits.remainingOrders <= 50 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '1rem',
                backgroundColor: orderLimits.remainingOrders <= 10 ? '#fee' : '#fff3cd',
                border: `1px solid ${orderLimits.remainingOrders <= 10 ? '#f5c6cb' : '#ffeaa7'}`,
                borderRadius: '8px',
                marginTop: '1rem'
              }}>
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.5rem'
                }}>
                  <div style={{ 
                    fontSize: '1.2rem', 
                    fontWeight: 'bold', 
                    color: orderLimits.remainingOrders <= 10 ? '#721c24' : '#856404'
                  }}>
                    ⚠️ Order Limit Warning
                  </div>
                  <button
                    onClick={() => refreshOrderLimits(true)}
                    style={{
                      background: 'none',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      color: '#666'
                    }}
                    title="Refresh order limits"
                  >
                    🔄
                  </button>
                </div>
                <div style={{ 
                  fontSize: '1rem', 
                  color: orderLimits.remainingOrders <= 10 ? '#721c24' : '#856404',
                  marginTop: '0.5rem'
                }}>
                  Only {orderLimits.remainingOrders} orders remaining today
                </div>
                <div style={{ 
                  fontSize: '0.9rem', 
                  color: orderLimits.remainingOrders <= 10 ? '#721c24' : '#856404',
                  marginTop: '0.25rem',
                  opacity: 0.8
                }}>
                  {orderLimits.ordersInLast24Hours}/{orderLimits.maxOrdersPerDay} used
                </div>
                <div style={{ 
                  fontSize: '0.8rem', 
                  color: orderLimits.remainingOrders <= 10 ? '#721c24' : '#856404',
                  marginTop: '0.5rem',
                  opacity: 0.7
                }}>
                  Contact: vadym@suprun.ca
                </div>
                <div style={{ 
                  fontSize: '0.8rem', 
                  color: orderLimits.remainingOrders <= 10 ? '#721c24' : '#856404',
                  marginTop: '0.25rem',
                  opacity: 0.7
                }}>
                  URL: {window.location.origin}{window.location.pathname.split('/').slice(0, 2).join('/')}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Order History Table */}
      {getCompletedOrdersHistory().length > 0 && (
        <div className="card" style={{ marginTop: '2rem' }}>
          <div className="card-header">
            <h3 className="card-title">Completed Orders History</h3>
            <small style={{ color: '#666', marginTop: '0.5rem', display: 'block' }}>
              Showing all completed orders (not limited to 12 hours)
            </small>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Order ID</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Customer</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Items</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Total</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Completed Time</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Service</th>
                </tr>
              </thead>
              <tbody>
                {getCompletedOrdersHistory().map(order => (
                  <tr key={order.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: '0.75rem', fontFamily: 'monospace', fontWeight: '500' }}>
                      #{order.id.slice(-8).toUpperCase()}
                    </td>
                    <td style={{ padding: '0.75rem' }}>{order.customerName}</td>
                    <td style={{ padding: '0.75rem' }}>
                      {order.items.map(item => `${item.quantity}× ${item.menuItem?.name || 'Unknown Item'}`).join(', ')}
                    </td>
                    <td style={{ padding: '0.75rem', fontWeight: '500' }}>
                      ${(order.total || order.totalAmount || 0).toFixed(2)}
                    </td>
                    <td style={{ padding: '0.75rem', color: '#666' }}>
                      {formatTime(order.timestamp)}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {order.serviceType === 'dine-in' ? 'Dine In' : 'Takeout'}
                      {order.tableName && ` (${order.tableName})`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Order Modal */}
      {showEditModal && editingOrder && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, color: '#333' }}>Edit Order #{editingOrder.id.slice(-8).toUpperCase()}</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingOrder(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0.5rem'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ marginBottom: '0.5rem', color: '#333' }}>Order Details</h3>
              <div style={{ 
                padding: '1rem', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.9rem' }}>
                  <div><strong>Customer:</strong> {editingOrder.customerName}</div>
                  <div><strong>Service:</strong> {editingOrder.serviceType === 'dine-in' ? 'Dine In' : 'Takeout'}</div>
                  <div><strong>Table:</strong> {editingOrder.tableName || 'N/A'}</div>
                  <div><strong>Total:</strong> ${(editingOrder.total || editingOrder.totalAmount || 0).toFixed(2)}</div>
                  <div><strong>Status:</strong> {editingOrder.status}</div>
                  <div><strong>Time:</strong> {formatTime(editingOrder.timestamp)}</div>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ marginBottom: '0.5rem', color: '#333' }}>Order Items</h3>
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {editingOrder.items.map((item, index) => (
                  <div key={index} style={{
                    padding: '0.75rem',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    marginBottom: '0.5rem',
                    backgroundColor: 'white'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '600' }}>{item.quantity}× {item.menuItem?.name || 'Unknown Item'}</span>
                      <span style={{ color: '#666' }}>${((item.menuItem?.price || 0) * item.quantity).toFixed(2)}</span>
                    </div>
                    {item.specialRequests && (
                      <div style={{ 
                        fontSize: '0.85rem', 
                        color: '#e74c3c', 
                        marginTop: '0.25rem',
                        fontStyle: 'italic'
                      }}>
                        Note: {item.specialRequests}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {(editingOrder.specialRequests || editingOrder.notes) && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ marginBottom: '0.5rem', color: '#333' }}>
                  {editingOrder.source === 'table-order' ? 'Customer Notes' : 'Special Requests'}
                </h3>
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#fff3cd',
                  borderRadius: '6px',
                  border: '1px solid #ffeaa7'
                }}>
                  {editingOrder.notes || editingOrder.specialRequests}
                </div>
              </div>
            )}

            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              justifyContent: 'flex-end',
              borderTop: '1px solid #dee2e6',
              paddingTop: '1rem'
            }}>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingOrder(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  color: '#666',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // TODO: Implement order update functionality
                  alert('Order update functionality will be implemented in the next phase');
                  setShowEditModal(false);
                  setEditingOrder(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '600'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Confirmation Modal */}
      {confirmRejectOrder && (
        <div role="dialog" aria-modal="true" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          padding: '16px'
        }}>
          <div style={{
            width: '100%',
            maxWidth: 520,
            background: 'white',
            borderRadius: 12,
            border: '1px solid #e2e8f0',
            boxShadow: '0 16px 40px rgba(0,0,0,0.25)',
            overflow: 'hidden'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', borderBottom: '1px solid #e9ecef' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.25rem' }}>⚠️</span>
                <h3 style={{ margin: 0 }}>Reject order #{confirmRejectOrder.id.slice(-8).toUpperCase()}</h3>
              </div>
              <button onClick={() => setConfirmRejectOrder(null)} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            <div style={{ padding: '1rem 1.25rem' }}>
              <p style={{ marginTop: 0, color: '#374151' }}>This will permanently remove the order from the kitchen. This action cannot be undone.</p>
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <strong>Customer:</strong>
                  <span>{confirmRejectOrder.customerName || 'Unknown'}</span>
                </div>
                {confirmRejectOrder.tableName && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <strong>Table:</strong>
                    <span>{confirmRejectOrder.tableName}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <strong>Total:</strong>
                  <span>${(confirmRejectOrder.total || confirmRejectOrder.totalAmount || 0).toFixed(2)}</span>
                </div>
                <div>
                  <strong>Items:</strong>
                  <ul style={{ margin: '0.5rem 0 0 1rem', padding: 0 }}>
                    {confirmRejectOrder.items.slice(0, 5).map((i, idx) => (
                      <li key={idx}>{i.quantity}× {i.menuItem?.name || 'Item'}</li>
                    ))}
                    {confirmRejectOrder.items.length > 5 && (
                      <li>+{confirmRejectOrder.items.length - 5} more</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '0 1.25rem 1rem 1.25rem', borderTop: '1px solid #e9ecef' }}>
              <button onClick={() => setConfirmRejectOrder(null)} style={{ padding: '0.5rem 0.9rem', border: '1px solid #d1d5db', background: 'white', color: '#374151', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
              <button onClick={() => { deleteOrder(confirmRejectOrder.id); setConfirmRejectOrder(null); }} style={{ padding: '0.5rem 0.9rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Reject Order</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KitchenCabinet; 